import { TCanvasNode } from "../../types/TCanvasNode";

/**
 * Clean Circular Layout Engine - Rebuild from scratch
 * 
 * Hierarchical Layout:
 * 1. Domains arranged in a circle around canvas center
 * 2. Tables arranged in a circle around their parent domain
 * 3. Columns arranged in a circle around their parent table
 * 
 * Features:
 * - Pure mathematical positioning (predictable)
 * - Gentle spring forces for draggability with snap-back
 * - Auto-adjusting radii based on child count
 * - No overlaps between domain clusters
 */

export function cleanCircularLayout() {
  let nodes: TCanvasNode[] = [];
  let centerX = 0;
  let centerY = 0;
  let domainSpacing = 800;
  let tableRadiusBase = 200;
  let columnRadiusBase = 80;
  let snapBackStrength = 0.15;
  let initialized = false;
  let enableSnapBack = false;
  let tableForceStrength = 0.1; // Minimum for smoothest movement
  let columnForceStrength = 0.4;

  function force(alpha: number) {
    // Group nodes by hierarchy
    const domains: TCanvasNode[] = [];
    const tablesByDomain: Map<string, TCanvasNode[]> = new Map();
    const orphanTables: TCanvasNode[] = []; // Tables without parent domain
    const columnsByTable: Map<string, TCanvasNode[]> = new Map();

    nodes.forEach((node) => {
      if (node.node_type === "DOMAIN") {
        domains.push(node);
      } else if (node.node_type === "TABLE") {
        const parentId = node.parent_node_id;
        if (parentId) {
          if (!tablesByDomain.has(parentId)) {
            tablesByDomain.set(parentId, []);
          }
          tablesByDomain.get(parentId)!.push(node);
        } else {
          // Table without parent domain - spread these out
          orphanTables.push(node);
        }
      } else if (node.node_type === "COLUMN" || node.node_type === "OTHER") {
        const parentId = node.parent_node_id;
        if (parentId) {
          if (!columnsByTable.has(parentId)) {
            columnsByTable.set(parentId, []);
          }
          columnsByTable.get(parentId)!.push(node);
        }
      }
    });

    // On first run, set initial positions directly for immediate effect
    if (!initialized && alpha > 0.3) {
      initialized = true;
      
      // SPECIAL CASE: If no domains exist, position orphan tables randomly with collision detection
      if (domains.length === 0 && orphanTables.length > 0) {
        // Position tables randomly with minimum spacing to prevent overlaps
        const minSpacing = 250; // Minimum distance between tables (including their column circles)
        const placedTables: Array<{ x: number; y: number }> = [];
        
        orphanTables.forEach((table) => {
          let validPosition = false;
          let attempts = 0;
          let randomX = 0;
          let randomY = 0;
          
          // Try to find a non-overlapping position
          while (!validPosition && attempts < 50) {
            randomX = centerX + (Math.random() - 0.5) * 1200;
            randomY = centerY + (Math.random() - 0.5) * 1200;
            
            // Check if this position overlaps with any already placed table
            validPosition = placedTables.every(placed => {
              const dx = randomX - placed.x;
              const dy = randomY - placed.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              return distance >= minSpacing;
            });
            
            attempts++;
          }
          
          table.x = randomX;
          table.y = randomY;
          table.vx = 0;
          table.vy = 0;
          
          placedTables.push({ x: randomX, y: randomY });
        });
        
        // Position columns in circles around their randomly placed tables
        const tablePositions: Map<string, { x: number; y: number }> = new Map();
        orphanTables.forEach((table) => {
          if (table.x != null && table.y != null) {
            tablePositions.set(table.node_id, { x: table.x, y: table.y });
          }
        });
        
        columnsByTable.forEach((columns, tableId) => {
          const tablePos = tablePositions.get(tableId);
          if (!tablePos) return;

          const numColumns = columns.length;
          const radius = columnRadiusBase + (numColumns * 5);

          columns.forEach((column, index) => {
            const angle = (2 * Math.PI * index) / numColumns;
            column.x = tablePos.x + radius * Math.cos(angle);
            column.y = tablePos.y + radius * Math.sin(angle);
            column.vx = 0;
            column.vy = 0;
          });
        });
        
        return; // Skip the rest of initialization for this case
      }
      
      // LEVEL 1: Position domains in a circle
      const numDomains = domains.length;
      domains.forEach((domain, index) => {
        const angle = (2 * Math.PI * index) / numDomains;
        domain.x = centerX + domainSpacing * Math.cos(angle);
        domain.y = centerY + domainSpacing * Math.sin(angle);
        domain.vx = 0;
        domain.vy = 0;
      });

      // Store domain positions
      const domainPositions: Map<string, { x: number; y: number }> = new Map();
      domains.forEach((domain) => {
        if (domain.x != null && domain.y != null) {
          domainPositions.set(domain.node_id, { x: domain.x, y: domain.y });
        }
      });

      // LEVEL 2: Position tables in circles around domains with dynamic radius
      tablesByDomain.forEach((tables, domainId) => {
        const domainPos = domainPositions.get(domainId);
        if (!domainPos) return;

        const numTables = tables.length;
        // Dynamic radius: base + scaling factor based on table count
        // More tables = larger circle to prevent overlap
        const radius = tableRadiusBase + (numTables * 30);

        tables.forEach((table, index) => {
          const angle = (2 * Math.PI * index) / numTables;
          table.x = domainPos.x + radius * Math.cos(angle);
          table.y = domainPos.y + radius * Math.sin(angle);
          table.vx = 0;
          table.vy = 0;
        });
      });

      // Store table positions
      const tablePositions: Map<string, { x: number; y: number }> = new Map();
      nodes.forEach((node) => {
        if (node.node_type === "TABLE" && node.x != null && node.y != null) {
          tablePositions.set(node.node_id, { x: node.x, y: node.y });
        }
      });

      // LEVEL 3: Position columns in circles around tables
      columnsByTable.forEach((columns, tableId) => {
        const tablePos = tablePositions.get(tableId);
        if (!tablePos) return;

        const numColumns = columns.length;
        const radius = columnRadiusBase + (numColumns * 5);

        columns.forEach((column, index) => {
          const angle = (2 * Math.PI * index) / numColumns;
          column.x = tablePos.x + radius * Math.cos(angle);
          column.y = tablePos.y + radius * Math.sin(angle);
          column.vx = 0;
          column.vy = 0;
        });
      });
      
      return; // Skip velocity updates on initialization frame
    }

    // After initialization, maintain overall structure with VERY gentle forces
    
    // SPECIAL CASE: If no domains exist, maintain orphan tables in their positions
    // (They were randomly positioned on init, just maintain column circles)
    if (domains.length === 0 && orphanTables.length > 0) {
      // Tables stay wherever they are (random positions), just update columns
      const tablePositions: Map<string, { x: number; y: number }> = new Map();
      orphanTables.forEach((table) => {
        if (table.x != null && table.y != null) {
          tablePositions.set(table.node_id, { x: table.x, y: table.y });
        }
      });
      
      // Position columns around orphan tables with strong force
      columnsByTable.forEach((columns, tableId) => {
        const tablePos = tablePositions.get(tableId);
        if (!tablePos) return;

        const numColumns = columns.length;
        const radius = columnRadiusBase + (numColumns * 5);

        columns.forEach((column, index) => {
          const angle = (2 * Math.PI * index) / numColumns;
          const targetX = tablePos.x + radius * Math.cos(angle);
          const targetY = tablePos.y + radius * Math.sin(angle);

          if (column.x != null && column.y != null) {
            const dx = targetX - column.x;
            const dy = targetY - column.y;
            column.vx = (column.vx || 0) + dx * 0.7 * alpha;
            column.vy = (column.vy || 0) + dy * 0.7 * alpha;
          }
        });
      });
      
      return; // Skip the domain-based logic
    }
    
    // LEVEL 1: Domains gently pulled toward their calculated circular positions
    // This maintains structure while allowing manual positioning
    const numDomains = domains.length;
    domains.forEach((domain, index) => {
      // Skip if node is being dragged or pinned
      if (domain.fx != null || domain.fy != null) return;
      
      const angle = (2 * Math.PI * index) / numDomains;
      const targetX = centerX + domainSpacing * Math.cos(angle);
      const targetY = centerY + domainSpacing * Math.sin(angle);

      if (domain.x != null && domain.y != null) {
        const dx = targetX - domain.x;
        const dy = targetY - domain.y;
        // Very weak force only if snap-back is enabled
        const structureForce = enableSnapBack ? snapBackStrength : 0;
        if (structureForce > 0) {
          domain.vx = (domain.vx || 0) + dx * structureForce * alpha;
          domain.vy = (domain.vy || 0) + dy * structureForce * alpha;
        }
      }
    });

    // Store domain positions (wherever they currently are)
    const domainPositions: Map<string, { x: number; y: number }> = new Map();
    domains.forEach((domain) => {
      if (domain.x != null && domain.y != null) {
        domainPositions.set(domain.node_id, { x: domain.x, y: domain.y });
      }
    });

    // LEVEL 2: Tables follow their domain with SMOOTH movement (not instant)
    // Use moderate force strength for smooth, fluid follow behavior
    tablesByDomain.forEach((tables, domainId) => {
      const domainPos = domainPositions.get(domainId);
      if (!domainPos) return;

      const numTables = tables.length;
      // Dynamic radius: more tables = larger circle
      const radius = tableRadiusBase + (numTables * 30);

      tables.forEach((table, index) => {
        // Skip if node is being dragged or pinned
        if (table.fx != null || table.fy != null) return;
        
        const angle = (2 * Math.PI * index) / numTables;
        const targetX = domainPos.x + radius * Math.cos(angle);
        const targetY = domainPos.y + radius * Math.sin(angle);

        if (table.x != null && table.y != null) {
          const dx = targetX - table.x;
          const dy = targetY - table.y;
          // Use user-adjustable force strength
          table.vx = (table.vx || 0) + dx * tableForceStrength * alpha;
          table.vy = (table.vy || 0) + dy * tableForceStrength * alpha;
        }
      });
    });

    // Store table positions for column positioning
    const tablePositions: Map<string, { x: number; y: number }> = new Map();
    nodes.forEach((node) => {
      if (node.node_type === "TABLE" && node.x != null && node.y != null) {
        tablePositions.set(node.node_id, { x: node.x, y: node.y });
      }
    });

    // LEVEL 3: Columns follow their table with SMOOTH movement (not instant)
    // Use moderate force strength for smooth, fluid follow behavior
    columnsByTable.forEach((columns, tableId) => {
      const tablePos = tablePositions.get(tableId);
      if (!tablePos) return;

      const numColumns = columns.length;
      const radius = columnRadiusBase + (numColumns * 5);

      columns.forEach((column, index) => {
        // Skip if node is being dragged or pinned
        if (column.fx != null || column.fy != null) return;
        
        const angle = (2 * Math.PI * index) / numColumns;
        const targetX = tablePos.x + radius * Math.cos(angle);
        const targetY = tablePos.y + radius * Math.sin(angle);

        if (column.x != null && column.y != null) {
          const dx = targetX - column.x;
          const dy = targetY - column.y;
          // Use user-adjustable force strength
          column.vx = (column.vx || 0) + dx * columnForceStrength * alpha;
          column.vy = (column.vy || 0) + dy * columnForceStrength * alpha;
        }
      });
    });
  }

  force.initialize = function(_nodes: TCanvasNode[], ...args: any[]) {
    nodes = _nodes;
    initialized = false; // Reset initialization flag when nodes change
  };

  force.center = function(x: number, y: number) {
    centerX = x;
    centerY = y;
    return force;
  };

  force.domainSpacing = function(spacing: number) {
    domainSpacing = spacing;
    return force;
  };

  force.tableRadius = function(radius: number) {
    tableRadiusBase = radius;
    return force;
  };

  force.columnRadius = function(radius: number) {
    columnRadiusBase = radius;
    return force;
  };

  force.snapBackStrength = function(strength: number) {
    snapBackStrength = strength;
    return force;
  };

  force.enableSnapBack = function(enable: boolean) {
    enableSnapBack = enable;
    return force;
  };

  force.tableForceStrength = function(strength: number) {
    tableForceStrength = strength;
    return force;
  };

  force.columnForceStrength = function(strength: number) {
    columnForceStrength = strength;
    return force;
  };

  return force;
}
